/**
 * Shared clock bits for the three countdown views: a tick that lands on the
 * wall clock's second boundary, the seconds count's every-other-second pulse,
 * and the progress row's rotating "… left" label.
 */

/**
 * Runs `fn` just after each wall-clock second, so the seconds
 * count changes on the boundary and never skips or repeats a number the way
 * a free-running 1000ms interval drifts into. Returns a stop function.
 */
export function everySecond(fn: () => void): () => void {
  let timer = 0;
  let stopped = false;
  const schedule = () => {
    if (stopped) return;
    timer = window.setTimeout(() => {
      fn();
      schedule();
    }, 1000 - (Date.now() % 1000) + 15);
  };
  // A suspended Home Screen app resumes mid-second; catch up at once.
  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    window.clearTimeout(timer);
    fn();
    schedule();
  };
  document.addEventListener("visibilitychange", onVisible);
  schedule();
  return () => {
    stopped = true;
    window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/**
 * The seconds count lights on every even second and fades back on the odd
 * one. The fade itself is a CSS transition on `.lit`, so the view only says
 * which half of the beat it's in. Under Reduce Motion the number stays lit.
 */
export function pulse(el: HTMLElement, reducedMotion: boolean, now = Date.now()) {
  el.classList.toggle("lit", reducedMotion || Math.floor(now / 1000) % 2 === 0);
}

type Unit = "months" | "weeks" | "hours" | "minutes" | "seconds";
const ORDER: Unit[] = ["months", "weeks", "hours", "minutes", "seconds"];
/** How long each unit stays up before the label moves on. */
const HOLD = 4;

function monthsBetween(from: Date, to: Date): number {
  let m = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  const probe = new Date(from);
  probe.setMonth(probe.getMonth() + m);
  if (probe > to) m--;
  return Math.max(0, m);
}

/** Whole units left, largest first, leaving out any there isn't at least one of. */
export function unitsLeft(now: number, target: number): { unit: Unit; n: number }[] {
  const ms = Math.max(0, target - now);
  const n: Record<Unit, number> = {
    months: monthsBetween(new Date(now), new Date(target)),
    weeks: Math.floor(ms / 604800000),
    hours: Math.floor(ms / 3600000),
    minutes: Math.floor(ms / 60000),
    seconds: Math.floor(ms / 1000),
  };
  return ORDER.filter((u) => n[u] >= 1).map((u) => ({ unit: u, n: n[u] }));
}

/**
 * The progress row's right-hand label. It steps through months, weeks,
 * hours, minutes and seconds left, a few seconds each, skipping any unit the
 * countdown has run below (no months once it's under a month). The number
 * keeps ticking while it's up.
 */
export function createTimeLeft(el: HTMLElement, fmt: (n: number) => string, reducedMotion: boolean) {
  let current: Unit | null = null;
  let shownFor = 0;
  return {
    update(now: number, target: number) {
      const units = unitsLeft(now, target);
      if (!units.length) {
        el.textContent = "no seconds left";
        current = null;
        return;
      }
      let idx = units.findIndex((u) => u.unit === current);
      if (idx < 0 || ++shownFor >= HOLD) {
        const next = idx < 0 ? 0 : (idx + 1) % units.length;
        if (next !== idx || idx < 0) {
          shownFor = 0;
          if (current && !reducedMotion) {
            el.animate([{ opacity: 0, transform: "translateY(3px)" }, { opacity: 1, transform: "none" }], { duration: 280, easing: "ease-out" });
          }
        }
        idx = next;
        current = units[idx].unit;
      }
      const { unit, n } = units[idx];
      el.textContent = `${fmt(n)} ${n === 1 ? unit.slice(0, -1) : unit} left`;
    },
  };
}
